import { useEffect, useRef, useState } from 'react'
import { drawMap, P, plans } from '../../lib/geo'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { safeDpr } from '../../lib/canvas'
import './World.css'

const CENTER = P(28.585, 77.205)
const OVERSCAN = 0.15 // the map is 30% larger than the screen so it can drift without showing edges

interface Dot { id: string; x: number; y: number; d: number }

/** The fixed night map of Delhi NCR that every glass panel floats over. */
export function World() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const pinsRef = useRef<HTMLDivElement>(null)
  const [dots, setDots] = useState<Dot[]>([])
  const reduced = useReducedMotion()

  // Draw the map once, and again (debounced) when the window size changes.
  useEffect(() => {
    let timer = 0
    let lastW = 0
    let lastH = 0
    const draw = () => {
      const c = canvasRef.current
      const ctx = c?.getContext('2d')
      if (!c || !ctx) return
      const w = window.innerWidth * (1 + 2 * OVERSCAN)
      const h = window.innerHeight * (1 + 2 * OVERSCAN)
      // It sits behind blurred glass, so 6M pixels is plenty even on a 5K display.
      const dpr = safeDpr(w, h, 1.5, 6_000_000)
      lastW = window.innerWidth
      lastH = window.innerHeight
      const s = Math.max(w / 1650, h / 1300)
      c.width = Math.round(w * dpr)
      c.height = Math.round(h * dpr)
      try {
        drawMap(ctx, { s, dpr, vw: w, vh: h, cx: CENTER.x, cy: CENTER.y })
      } catch (err) {
        console.error('[mauj] Background map failed to draw.', err)
      }
      setDots(plans.map((p, i) => ({ id: p.id, x: w / 2 + (p.x - CENTER.x) * s, y: h / 2 + (p.y - CENTER.y) * s, d: (i * 0.37) % 3.4 })))
    }
    draw()
    const onResize = () => {
      // Phones resize the window whenever the address bar slides in or out. The map is drawn 30% larger
      // than the screen, so only redraw when the width changes or the height changes a lot (rotation).
      const dw = Math.abs(window.innerWidth - lastW)
      const dh = Math.abs(window.innerHeight - lastH)
      if (dw < 2 && dh < lastH * 0.25) return
      window.clearTimeout(timer)
      timer = window.setTimeout(draw, 160)
    }
    window.addEventListener('resize', onResize)
    document.fonts?.ready.then(draw).catch(() => undefined)
    return () => {
      window.removeEventListener('resize', onResize)
      window.clearTimeout(timer)
    }
  }, [])

  // Gentle parallax: the map drifts up as you scroll and leans away from the mouse.
  useEffect(() => {
    let mx = 0
    let my = 0
    let raf = 0
    const apply = () => {
      raf = 0
      const ty = reduced ? 0 : -Math.min(window.scrollY * 0.06, window.innerHeight * 0.14) + my
      const t = `translate3d(${mx.toFixed(1)}px, ${ty.toFixed(1)}px, 0)`
      if (canvasRef.current) canvasRef.current.style.transform = t
      if (pinsRef.current) pinsRef.current.style.transform = t
    }
    const queue = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }
    const onMove = (e: PointerEvent) => {
      if (reduced || e.pointerType !== 'mouse') return
      mx = -(e.clientX / window.innerWidth - 0.5) * 18
      my = -(e.clientY / window.innerHeight - 0.5) * 12
      queue()
    }
    apply()
    window.addEventListener('scroll', queue, { passive: true })
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('scroll', queue)
      window.removeEventListener('pointermove', onMove)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [reduced])

  return (
    <div id="world" aria-hidden="true">
      <canvas id="mapc" ref={canvasRef} />
      <div id="pinsL" ref={pinsRef}>
        {dots.map((d) => (
          <i key={d.id} className="wp" style={{ left: d.x, top: d.y, ['--d' as string]: `${d.d.toFixed(2)}s` }} />
        ))}
      </div>
      <div className="orb a" />
      <div className="orb b" />
      <div className="orb c" />
      <div id="veil" />
      <div id="grain" />
    </div>
  )
}
